import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
import {AgentIcon} from '@primer/octicons-react'
import {ActionList, Button, Spinner, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type FC, useCallback, useMemo, useState} from 'react'
import {getFindingFileLocation, getFindingSeverity, getFindingTitle} from './entity-helpers'
import styles from './Finding.module.css'
import type {Finding} from './types'

interface FixFindingDialogProps {
  findings: Finding[]
  basePath: string
  onClose: () => void
}

interface FixResponse {
  task_id: string
  task_url: string
}

export const FixFindingDialog: FC<FixFindingDialogProps> = ({findings, basePath, onClose}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(findings.map(f => f.id)))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<FixResponse | null>(null)
  const [instructions, setInstructions] = useState('')

  const selectedFindings = useMemo(() => findings.filter(f => selectedIds.has(f.id)), [findings, selectedIds])

  const handleToggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        findings: selectedFindings.map(f => ({
          id: f.id,
          message: f.message,
          filePath: f.filePath,
          startLine: f.startLine,
          endLine: f.endLine,
        })),
      }
      if (instructions) body.instructions = instructions

      const response = await verifiedFetchJSON(`${basePath}/code_review_findings/fix`, {
        method: 'POST',
        body,
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error ?? 'Failed to create fix task')
      }
      setResult(await response.json())
    } catch {
      // TODO: error handling
    } finally {
      setIsSubmitting(false)
    }
  }, [basePath, selectedFindings, instructions])

  if (result) {
    return (
      <Dialog title="Fix with Copilot" onClose={onClose}>
        <p>
          {`Copilot is fixing ${selectedFindings.length} ${selectedFindings.length === 1 ? 'finding' : 'findings'}.`}
        </p>
        <Button as="a" href={result.task_url} leadingVisual={AgentIcon} className="tmp-mt-2">
          View Copilot task
        </Button>
      </Dialog>
    )
  }

  return (
    <Dialog
      title="Fix with Copilot"
      onClose={onClose}
      footerButtons={[
        {
          buttonType: 'primary',
          content: isSubmitting ? (
            <>
              <Spinner size="small" /> Fixing…
            </>
          ) : (
            `Fix ${selectedIds.size} ${selectedIds.size === 1 ? 'finding' : 'findings'} with Copilot`
          ),
          onClick: () => void handleSubmit(),
          disabled: selectedIds.size === 0 || isSubmitting,
          leadingVisual: isSubmitting ? undefined : AgentIcon,
        },
      ]}
    >
      <>
        <p className="tmp-mb-3 text-medium text-semibold color-fg-muted">
          Implement the suggestions in the findings below with Copilot coding agent. You will be added as co-author on
          any new commits.
        </p>
        <Textarea
          block
          rows={4}
          resize="none"
          value={instructions}
          className="rounded-3"
          onChange={e => setInstructions(e.target.value)}
          aria-label="Additional instructions"
          placeholder="Add additional instructions..."
        />
        <p className="tmp-mt-3 tmp-mb-1 text-small text-semibold color-fg-muted">
          {`${selectedIds.size} ${selectedIds.size === 1 ? 'finding' : 'findings'}`}
        </p>
        <ActionList
          showDividers
          role="listbox"
          selectionVariant="multiple"
          aria-label="Findings to fix"
          className="mt-2 border rounded-3"
        >
          {findings.map(f => {
            const severity = getFindingSeverity(f)
            const fileLocation = getFindingFileLocation(f)
            return (
              <ActionList.Item key={f.id} selected={selectedIds.has(f.id)} onSelect={() => handleToggle(f.id)}>
                <span>{getFindingTitle(f)}</span>
                <ActionList.Description variant="block">
                  <div className="d-flex align-center gap-1">
                    {severity && <RuleSeverityBadge severity={severity} />}
                    {fileLocation && (
                      <>
                        <span className={styles.metaSeparator}>{'\u00B7'}</span>
                        <span className={styles.fileLocation}>{fileLocation}</span>
                      </>
                    )}
                  </div>
                </ActionList.Description>
              </ActionList.Item>
            )
          })}
        </ActionList>
      </>
    </Dialog>
  )
}
